import { VisioBasketAction } from '@agent/core/basket';
import { IBasketStore } from '@agent/core/basket/basket.store';
import { AppointmentType } from '@agent/core/basket/visio/visio-basket.model';
import { ModalParamAppointmentRescheduling } from '@agent/core/contact/appointment-deprecated.service';
import { AppointmentService, ModalParamAppointmentCancellation } from '@agent/core/contact/appointment.service';
import { AppointmentCancellationDialogComponent } from '@agent/core/contact/appointment/appointment-cancellation-dialog.component';
import { AppointmentInvitationDialogComponent } from '@agent/core/contact/appointment/appointment-invitation-dialog.component';
import { AppointmentReschedulingDialogComponent } from '@agent/core/contact/appointment/appointment-rescheduling-dialog.component';
import { AppointmentTransferDialogComponent } from '@agent/core/contact/appointment/transfer-popup/appointment-transfer-dialog.component';
import { REFRESH_INPROCESS_INTERACTIONS } from '@agent/core/contact/history/models/history-models';
import { Injectable, Injector } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NG1_BASKET_STORE } from '@commons/core/core.provider.ajs';
import { IVisioAppointmentTransfer, VisioAppointmentDetail } from '@commons/models/appointment';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { first, map, switchMap, tap } from 'rxjs';

import {
  AppointmentAction,
  AppointmentDispatcher,
  CngAgentEffect,
  InviteRequestDto,
  isNotNullOrUndefined,
  MessagingService,
  RescheduleRequestDto,
  toActionError,
  ToasterFacade,
  TranslateService,
} from '@contact/upgrade';

@Injectable({
  providedIn: 'root',
})
export class AppointmentEffect extends CngAgentEffect {
  /**
   * Remind me effect
   */
  remindMe$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppointmentAction.RemindMe),
      switchMap(({ id }) =>
        this.appointmentService.remindMe(id).pipe(
          // success
          map(() => this.appointmentDispatcher.remindMeSuccess(id)),
          // fail
          toActionError(err => this.appointmentDispatcher.remindMeFail(id, err))
        )
      )
    )
  );

  /**
   * Remind not me effect
   */
  remindNotMe$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppointmentAction.RemindNotMe),
      switchMap(({ id }) =>
        this.appointmentService.notRemindMe(id).pipe(
          // success
          map(() => this.appointmentDispatcher.remindNotMeSuccess(id)),
          // fail
          toActionError(err => this.appointmentDispatcher.remindNotMeFail(id, err))
        )
      )
    )
  );

  /**
   * Join effect
   */
  join$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppointmentAction.Join),
      switchMap(({ id }) =>
        this.appointmentService
          .join({
            appointmentId: id,
          })
          .pipe(
            // success
            map(() => this.appointmentDispatcher.joinSuccess(id)),
            // fail
            toActionError(err => this.appointmentDispatcher.joinFail(id, err))
          )
      )
    )
  );

  /**
   * Open Reschedule Form Effect
   */
  openRescheduleForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppointmentAction.OpenRescheduleForm),
      switchMap(action =>
        this.dialog
          .open<any, ModalParamAppointmentRescheduling, RescheduleRequestDto>(AppointmentReschedulingDialogComponent, {
            data: {
              appointmentId: action.id,
              campaignId: action.campaignId,
              originalTimeSlot: {
                id: action.timeSlotId,
                beginDate: action.timeSlotBeginDate,
                endDate: action.timeSlotEndDate,
                type: action.timeSlotType,
                contactThreshold: action.contactThreshold,
                contactCount: action.contactCount,
              },
            },
          })
          .afterClosed()
          .pipe(
            isNotNullOrUndefined(),
            map((result: RescheduleRequestDto) =>
              this.appointmentDispatcher.reschedule(result.appointmentUuid, result.timeSlotUuid, result.comment)
            )
          )
      )
    )
  );

  /**
   * Reschedule/Cancel Success Effect
   */
  rescheduleSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppointmentAction.RescheduleSuccess, AppointmentAction.CancelSuccess),
        tap(action => {
          this.refreshVisioBasketAndCloseToast(action);
          // Refresh the count that give the number of appointments of the day of the csr
          this.appointmentService.getTodayAppointmentNumber();
        })
      ),
    { dispatch: false }
  );

  /**
   * open Cancel Form Effect
   */
  openCancelForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppointmentAction.OpenCancelForm),
      switchMap(action =>
        this.dialog
          .open<any, ModalParamAppointmentCancellation, ModalParamAppointmentCancellation>(AppointmentCancellationDialogComponent, {
            data: {
              appointmentId: action.id,
              status: action.status,
            },
          })
          .afterClosed()
          .pipe(
            isNotNullOrUndefined(),
            map((result: ModalParamAppointmentCancellation) => this.appointmentDispatcher.cancel(result.appointmentId, result.status))
          )
      )
    )
  );

  /**
   * Open Invite Form Effect and send an inviteParticipant Action on Close
   */
  openInviteForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppointmentAction.OpenInviteForm),
      switchMap(action =>
        this.dialog
          .open<any, { appointmentId: string }, InviteRequestDto>(AppointmentInvitationDialogComponent, {
            data: {
              appointmentId: action.id,
            },
          })
          .afterClosed()
          .pipe(
            isNotNullOrUndefined(),
            map((result: InviteRequestDto) => this.appointmentDispatcher.inviteParticipant(result))
          )
      )
    )
  );

  /**
   * Invite a participant Effect
   */
  inviteParticipant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppointmentAction.InviteParticipant),
      switchMap(action =>
        this.appointmentService.inviteParticipant(action.inviteRequest).pipe(
          map(() => this.appointmentDispatcher.InviteSuccess(action.inviteRequest.appointmentUuid)),
          // failure
          toActionError(err => this.appointmentDispatcher.InviteFail(action.inviteRequest.appointmentUuid, err))
        )
      )
    )
  );

  /**
   * Invite Participant Success Effect
   */
  inviteSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppointmentAction.InviteSuccess),
        tap(() => this.refreshVisioBasket())
      ),
    { dispatch: false }
  );

  /**
   * Invite Participant Fail Effect
   */
  inviteFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppointmentAction.InviteFail),
        tap(action => {
          if (typeof action.error === 'string') {
            const err = JSON.parse(action.error);
            if (!!err.inviteForbiddenMessage) {
              this.toasterFacade.alert({ message: err.inviteForbiddenMessage, duration: 10000 });
            }
          }
          this.refreshVisioBasket();
        })
      ),
    { dispatch: false }
  );

  /**
   * Reschedule Effect
   */
  reschedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppointmentAction.Reschedule),
      switchMap(({ id, timeSlotId, comment }) =>
        this.appointmentService
          .reschedule({
            appointmentUuid: id,
            timeSlotUuid: timeSlotId,
            comment: comment,
          })
          .pipe(
            // success
            map(() => this.appointmentDispatcher.rescheduleSuccess(id)),
            // failure
            toActionError(err => this.appointmentDispatcher.rescheduleFail(id, err))
          )
      )
    )
  );

  /**
   * Cancel Effect
   */
  cancel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppointmentAction.Cancel),
      switchMap(({ id, status }) =>
        this.appointmentService.cancel(id, status).pipe(
          // success
          map(() => this.appointmentDispatcher.cancelSuccess(id)),
          // failure
          toActionError(err => this.appointmentDispatcher.cancelFail(id, err))
        )
      )
    )
  );

  /**
   * Accept Appointment Effect
   */
  acceptAppointment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppointmentAction.AcceptAppointment),
      switchMap(action =>
        this.appointmentService
          .acceptAppointment(action.id, {
            uuid: action.csrUuid,
            login: action.csrLogin,
            firstName: action.csrFirstName,
            lastName: action.csrLastName,
          })
          .pipe(
            // success
            map(() => this.appointmentDispatcher.acceptSuccess(action.id, AppointmentType.APPOINTMENT)),
            // failure
            toActionError(err => this.appointmentDispatcher.acceptFail(action.id, AppointmentType.APPOINTMENT, err))
          )
      )
    )
  );

  /**
   * Accept Invitation Effect
   */
  acceptInvitation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppointmentAction.AcceptInvitation),
      switchMap(({ id, csrUuid }) =>
        this.appointmentService
          .acceptInvitation({
            appointmentUuid: id,
            authorUuid: csrUuid,
          })
          .pipe(
            // success
            map(() => this.appointmentDispatcher.acceptSuccess(id, AppointmentType.INVITE)),
            // failure
            toActionError(err => this.appointmentDispatcher.acceptFail(id, AppointmentType.INVITE, err))
          )
      )
    )
  );

  acceptFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppointmentAction.AcceptFail),
        tap(action => {
          if (action.appointmentType === AppointmentType.INVITE && typeof action.error === 'string') {
            const err = JSON.parse(action.error);
            if (!!err.acceptInvitationForbiddenMessage) {
              this.toasterFacade.notification({ message: err.acceptInvitationForbiddenMessage });
            }
          }
          this.refreshVisioBasket();
        })
      ),
    { dispatch: false }
  );

  /**
   * Refuse Invitation Effect
   */
  refuseInvitation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppointmentAction.RefuseInvitation),
      switchMap(({ id, csrUuid }) =>
        this.appointmentService
          .refuseInvitation({
            appointmentUuid: id,
            authorUuid: csrUuid,
          })
          .pipe(
            // success
            map(() => this.appointmentDispatcher.refuseInvitationSuccess(id)),
            // failure
            toActionError(err => this.appointmentDispatcher.refuseInvitationFail(id, err))
          )
      )
    )
  );

  /**
   * Accept Success Effect : manually refresh visio basket store and appointment counter
   */
  acceptSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppointmentAction.AcceptSuccess),
        tap(action => {
          this.refreshVisioBasket();
          if (action.appointmentType === AppointmentType.INVITE) {
            this.toasterFacade.notification({
              message: this.translateService.instant('agent:contact.appointment.visio.accept_invitation_success_message'),
              duration: 10000,
            });
          }
          this.appointmentService.getTodayAppointmentNumber();
        })
      ),
    { dispatch: false }
  );

  /**
   * Refuse Invitation Success Effect : manually refresh visio basket store
   */
  refuseInvitationSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppointmentAction.RefuseInvitationSuccess),
        tap(() => this.refreshVisioBasket())
      ),
    { dispatch: false }
  );

  /**
   * Open update dialog effect
   */
  openUpdateDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppointmentAction.OpenUpdateDialog),
      switchMap(({ appointmentId }) =>
        this.dialog
          .open(AppointmentTransferDialogComponent, {
            data: appointmentId,
          })
          .afterClosed()
          .pipe(
            isNotNullOrUndefined(),
            map((result: IVisioAppointmentTransfer) => this.appointmentDispatcher.update(VisioAppointmentDetail.toVisioTransferDto(result)))
          )
      )
    )
  );

  /**
   * Update/transfer Appointment Effect
   */
  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppointmentAction.Update),
      switchMap(({ appointment }) =>
        this.appointmentService.transferAppointment(appointment).pipe(
          // success
          map(() => this.appointmentDispatcher.updateSuccess(appointment.appointmentUuid)),
          // failure
          toActionError(err => this.appointmentDispatcher.updateFail(appointment.appointmentUuid, err))
        )
      )
    )
  );

  /**
   * Update Success Effect
   */
  updateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppointmentAction.UpdateSuccess),
        tap(() => {
          this.refreshVisioBasket();
          this.messagingService.forLocal().publish(REFRESH_INPROCESS_INTERACTIONS, undefined);
        })
      ),
    { dispatch: false }
  );

  /**
   * Update Fail Effect
   */
  updateFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppointmentAction.UpdateFail),
        tap(() => {
          this.toasterFacade.alert({ message: 'agent:toast.appointment_not_postponed' });
        })
      ),
    { dispatch: false }
  );

  /**
   * Initialize effect
   */
  init$ = createEffect(() => this.onInitEffects$.pipe(tap(() => this.appointmentService.getTodayAppointmentNumber())), { dispatch: false });

  private refreshVisioBasket() {
    (this.injector.get(NG1_BASKET_STORE) as IBasketStore).dispatch(new VisioBasketAction.ManualRefresh());
  }

  private refreshVisioBasketAndCloseToast(action) {
    this.refreshVisioBasket();
    this.toasterFacade.toasts$
      .pipe(
        first(),
        map(toasts =>
          toasts.filter(toast => toast.data && (toast.data as any).apointmentId && (toast.data as any).apointmentId === action.id)
        )
      )
      .subscribe(toasts => toasts.forEach(toast => this.toasterFacade.close(toast.id)));
  }

  constructor(
    private readonly actions$: Actions,
    private readonly appointmentDispatcher: AppointmentDispatcher,
    private readonly appointmentService: AppointmentService,
    private readonly injector: Injector,
    private readonly dialog: MatDialog,
    private readonly toasterFacade: ToasterFacade,
    private readonly translateService: TranslateService,
    private readonly messagingService: MessagingService
  ) {
    super();
  }
}
